// Hooks the Lead finder pages share.

import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api'
import { invalidateQueries, useQuery } from './query'

/** How often a page follows a run. The run itself waits three seconds per host. */
const POLL_MS = 2500

/** Re-ask every POLL_MS while `active`, and stop the moment it isn't. */
export function usePoll(active: boolean, refetch: () => void) {
  useEffect(() => {
    if (!active) return undefined
    const timer = setInterval(refetch, POLL_MS)
    return () => clearInterval(timer)
  }, [active, refetch])
}

export function useFinderStatus() {
  return useQuery(['growth', 'finder', 'status'], (signal) => api.growth.finder.status(signal))
}

/**
 * Follows a run over leads, wherever it was started: the run going now, or the last
 * one this page saw, so its result stays up once it ends. When it ends, every Growth
 * page reads again. `busy` is true for any run: a second one would be refused.
 * `adapter` is which kind this page shows: an activity check or a verification.
 */
export function useActivityRun(adapter: 'activity' | 'verify' = 'activity') {
  const { data: status, refetch: refetchStatus } = useFinderStatus()
  const activeId = status?.active_run_id ?? null
  const [lastId, setLastId] = useState<number | null>(null)
  useEffect(() => {
    if (activeId !== null) setLastId(activeId)
  }, [activeId])
  const id = activeId ?? lastId
  const { data, refetch } = useQuery(id ? ['growth', 'run', id] : null, (signal) => api.growth.finder.run(id as number, signal))
  const follow = useCallback(() => {
    refetch()
    refetchStatus()
  }, [refetch, refetchStatus])
  usePoll(activeId !== null, follow)

  const running = data?.run.status === 'running'
  const wasRunning = useRef(false)
  useEffect(() => {
    if (wasRunning.current && !running) invalidateQueries('growth')
    wasRunning.current = running
  }, [running])

  const run = data?.run.adapter === adapter && data.run.id === id ? data.run : null
  const stop = async () => {
    if (!run) return
    await api.growth.finder.stopRun(run.id)
    refetch()
  }
  /** After a start: this page goes on to follow that run. */
  const started = (runId: number) => {
    setLastId(runId)
    invalidateQueries('growth')
  }
  return { run, busy: activeId !== null, stop, started }
}
